import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge, Button, formatCurrency } from "@odingard/design-system";
import { ErrorState, LoadingState } from "@odingard/design-system";
import type { CatalogItem } from "@odingard/sdk-client";
import { useAsync } from "../../lib/useAsync";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { routeMeta, usePageMeta } from "../../lib/usePageMeta";

type Interval = "month" | "year";

const GROUP_LABELS: Record<CatalogItem["kind"], string> = {
  bundle: "Bundle",
  package: "Anchor package",
  addon: "Premium add on",
  app: "Single applications",
};
const GROUP_ORDER: CatalogItem["kind"][] = ["bundle", "package", "addon", "app"];

export function Pricing() {
  usePageMeta(routeMeta("/pricing"));
  const { data, loading, error, reload } = useAsync(() => api.launchCatalog(), []);
  const { data: config } = useAsync(() => api.billingConfig(), []);
  const [interval, setInterval] = useState<Interval>("month");
  const { user, activeOrg } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState<string | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  const liveCheckout = config?.liveCheckout ?? false;
  const items = useMemo(() => data?.items ?? [], [data]);

  async function buy(item: CatalogItem, mode: "live" | "demo") {
    if (!user || !activeOrg) {
      navigate(`/start?sku=${item.sku}&interval=${interval}`);
      return;
    }
    setBusy(`${item.sku}:${mode}`);
    setCheckoutError(null);
    try {
      if (mode === "live") {
        const { url } = await api.createStripeCheckout(activeOrg.id, { sku: item.sku, interval });
        window.location.assign(url);
      } else {
        const planId = `plan_${item.sku}`;
        const priceId = `price_${item.sku}_${interval === "year" ? "y" : "m"}`;
        const session = await api.createCheckoutSession(activeOrg.id, { planId, priceId, seats: 1 });
        navigate(`/checkout/${session.checkoutSessionId}`);
      }
    } catch (e) {
      setCheckoutError(e instanceof Error ? e.message : "Could not start checkout.");
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="section">
      <div className="container">
        <div className="section__head" style={{ textAlign: "center", margin: "0 auto var(--ods-space-6)" }}>
          <h1>Pricing for governed AI, per firm, not per seat</h1>
          <p>
            See real prices and start free today: no sales call, no per seat surprise. One firm, one price, every user.
            Start on one app, expand into the bundle. No card data ever touches our frontend.
          </p>
        </div>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: "var(--ods-space-4)" }}>
          <div className="billing-toggle" role="tablist" aria-label="Billing interval">
            <button role="tab" aria-selected={interval === "month"} className={interval === "month" ? "active" : ""} onClick={() => setInterval("month")}>
              Monthly
            </button>
            <button role="tab" aria-selected={interval === "year"} className={interval === "year" ? "active" : ""} onClick={() => setInterval("year")}>
              Annual · 2 months free
            </button>
          </div>
        </div>
        <p style={{ textAlign: "center", color: "var(--ods-color-muted)", fontSize: "var(--ods-text-sm)", marginBottom: "var(--ods-space-6)" }}>
          {liveCheckout
            ? "Secure checkout is powered by Stripe. You'll enter payment details on Stripe's hosted page."
            : "Live payments are not enabled in this environment. Use the demo checkout to preview the flow (no charge)."}
        </p>
        {checkoutError && <div className="banner banner--warning" role="alert">{checkoutError}</div>}
        {loading && <LoadingState />}
        {error && <ErrorState description={error.message} onRetry={reload} />}
        {data &&
          GROUP_ORDER.map((kind) => {
            const group = items.filter((it) => it.kind === kind);
            if (group.length === 0) return null;
            return (
              <div key={kind} style={{ marginBottom: "var(--ods-space-7)" }}>
                <h3 style={{ marginBottom: "var(--ods-space-4)" }}>{GROUP_LABELS[kind]}</h3>
                <div className="pricing-grid">
                  {group.map((item) => {
                    const price = item.prices[interval];
                    const featured = item.sku === "bundle-one";
                    const monthsFree = Math.round(12 - item.prices.year.unitAmount / item.prices.month.unitAmount);
                    return (
                      <div className={`plan-card ${featured ? "plan-card--featured" : ""}`} key={item.sku}>
                        {featured && <Badge tone="info">Most popular</Badge>}
                        {item.kind === "addon" && <Badge tone="warning">Add on</Badge>}
                        {!item.available && <Badge tone="neutral">Coming soon</Badge>}
                        <div>
                          <h3 style={{ margin: "0 0 4px" }}>{item.name}</h3>
                          <span style={{ color: "var(--ods-color-muted)", fontSize: "var(--ods-text-sm)" }}>{item.description}</span>
                        </div>
                        <div className="plan-card__price">
                          {item.kind === "addon" ? "+" : ""}
                          {formatCurrency(price.unitAmount, price.currency)}
                          <small> / {interval === "year" ? "yr" : "mo"}</small>
                        </div>
                        {monthsFree > 0 && (
                          <span style={{ color: "var(--ods-color-muted)", fontSize: "var(--ods-text-sm)" }}>
                            Save {monthsFree} months with annual
                          </span>
                        )}
                        {item.sku === "pkg-govcon" && (
                          <span style={{ color: "var(--ods-color-muted)", fontSize: "var(--ods-text-sm)" }}>
                            Less than the cost of one proposal desk hire.
                          </span>
                        )}
                        <div style={{ display: "grid", gap: "var(--ods-space-2)" }}>
                          <Button
                            block
                            variant={featured ? "primary" : "secondary"}
                            disabled={busy !== null || !item.available}
                            onClick={() => buy(item, liveCheckout ? "live" : "demo")}
                          >
                            {!item.available
                              ? "Coming soon"
                              : busy === `${item.sku}:${liveCheckout ? "live" : "demo"}`
                              ? "Starting…"
                              : !user
                                ? "Start"
                                : liveCheckout
                                  ? "Subscribe"
                                  : "Demo checkout"}
                          </Button>
                          {liveCheckout && user && activeOrg && item.available && (
                            <Button
                              block
                              variant="ghost"
                              disabled={busy !== null}
                              onClick={() => buy(item, "demo")}
                            >
                              {busy === `${item.sku}:demo` ? "Starting…" : "Preview demo (no charge)"}
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        <p style={{ textAlign: "center", color: "var(--ods-color-muted)", fontSize: "var(--ods-text-sm)" }}>
          Who this is not for: solo developers, and teams with no regulated or audited work. If nobody ever asks you to
          prove how an answer was reached, you do not need this.
        </p>
      </div>
    </section>
  );
}
